/**
 * @fileoverview Input/output schemas for the scene-prompt-creator-v1 agent.
 *
 * This agent generates a full image-gen prompt from prose and a locked
 * composition directive. Unlike story-image-prompt-creator-v2, it does
 * not make independent creative decisions about shot framing — it builds
 * the prompt around the user's chosen composition.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 * Same as story-image-prompt-creator-v2 plus a compositionDirective field.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.charactersCurrent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.worldElements.schema)
  .merge(AGENT_SCHEMA_OBJECTS.selectedText.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterContent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(z.object({
    compositionDirective: z.string().describe(
      'The chosen composition directive from the scene explorer. This is locked creative direction — the agent must build the image prompt around these choices without re-evaluating them.'
    ),
  }));

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Same output shape as story-image-prompt-creator-v2 for downstream compatibility.
 */
export const OutputSchema = z.object({
  shotFraming: z.string().describe('How the locked composition directive was realized in the prompt: the specific camera work, character placement, and lighting decisions that flesh out the chosen direction.'),
  prompt: z.string().describe('The generated, optimized prompt for an image generation model.'),
  characterNames: z.array(z.string()).optional().describe('An array of exact names of characters identified in the prose.'),
  worldElementNames: z.array(z.string()).optional().describe('An array of exact names of world elements identified in the prose.'),
});

export type Output = z.infer<typeof OutputSchema>;
